/**
 * Auth service for user-modern — ใช้ API เดียวกับ user-classic (/api/v1/user-auth)
 */
import { handleSessionReplaced } from '../utils/sessionReplacedHandler'
import { clearRuntimeTenantId, setRuntimeTenantId } from '../lib/runtimeTenant'
import { withApiBaseUrl } from '../lib/apiBaseUrl'

export interface LoginRequest {
  tenantId: string
  username: string
  password: string
  rememberMe?: boolean
  deviceId?: string
  deviceInfo?: { deviceType: string; browser: string; os: string; location?: string }
}

export interface LoginResponse {
  success: boolean
  message: string
  /** Set by backend when login is blocked (e.g. CONCURRENT_LOGIN_BLOCKED) */
  code?: string
  data?: {
    user: {
      id: string
      username: string
      firstName: string
      lastName: string
      mobile: string
      role: string
      status: string
      isMobileVerified: boolean
      tenantId: string
      lastLogin: string
    }
    tokens: { accessToken: string; refreshToken: string; expiresIn: number }
    deviceId: string
  }
  error?: string
}

export interface User {
  id: string
  username: string
  firstName: string
  lastName: string
  mobile: string
  role: string
  status: string
  isMobileVerified: boolean
  tenantId: string
  lastLogin: string
}

function getDeviceId(): string {
  let id = localStorage.getItem('deviceId')
  if (!id) {
    id = 'device_' + Date.now() + '_' + Math.random().toString(36).slice(2, 11)
    localStorage.setItem('deviceId', id)
  }
  return id
}

function getDeviceInfo() {
  const ua = navigator.userAgent
  return {
    deviceType: /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua) ? 'mobile' : 'desktop',
    browser: ua.includes('Chrome') ? 'Chrome' : ua.includes('Firefox') ? 'Firefox' : ua.includes('Safari') ? 'Safari' : 'Unknown',
    os: ua.includes('Windows') ? 'Windows' : ua.includes('Mac') ? 'macOS' : ua.includes('Android') ? 'Android' : ua.includes('iOS') ? 'iOS' : 'Unknown',
    location: 'unknown',
  }
}

export async function login(loginData: LoginRequest): Promise<LoginResponse> {
  const res = await fetch(withApiBaseUrl('/api/v1/user-auth/login'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Tenant-ID': loginData.tenantId,
      'X-Tenant-Domain': typeof window !== 'undefined' ? window.location.hostname : '',
    },
    body: JSON.stringify({
      tenantId: loginData.tenantId,
      username: loginData.username,
      password: loginData.password,
      deviceId: loginData.deviceId ?? getDeviceId(),
      deviceInfo: loginData.deviceInfo ?? getDeviceInfo(),
      rememberMe: loginData.rememberMe,
    }),
    credentials: 'include',
  })
  const data = (await res.json()) as LoginResponse
  if (!res.ok && data.code) {
    return data
  }
  if (data.success && data.data) {
    const storage = loginData.rememberMe ? localStorage : sessionStorage
    storage.setItem('accessToken', data.data.tokens.accessToken)
    storage.setItem('refreshToken', data.data.tokens.refreshToken)
    storage.setItem('deviceId', data.data.deviceId)
    storage.setItem('user', JSON.stringify(data.data.user))
    storage.setItem('tenantId', data.data.user.tenantId)
    setRuntimeTenantId(data.data.user.tenantId)
    if (loginData.rememberMe) {
      localStorage.setItem('rememberMe', 'true')
    } else {
      localStorage.removeItem('rememberMe')
    }
  }
  return data
}

/** Disconnect WebSocket so gateway emits user_offline and admin sees user as offline */
function disconnectWebSocket(): void {
  if (typeof window === 'undefined') return
  const win = window as unknown as { __huayWebSocket?: { disconnect: () => void; removeAllListeners: () => void; close: () => void } }
  if (win.__huayWebSocket) {
    try {
      win.__huayWebSocket.removeAllListeners?.()
      win.__huayWebSocket.disconnect?.()
      win.__huayWebSocket.close?.()
    } catch (e) {
      console.warn('Logout: WebSocket disconnect error', e)
    }
    win.__huayWebSocket = undefined
  }
}

export async function logout(): Promise<void> {
  const accessToken = getAccessToken()
  const storage = getAuthStorage()
  const refreshToken = storage.getItem('refreshToken')
  const deviceId = storage.getItem('deviceId')
  const tenantId = storage.getItem('tenantId') || ''

  // 1) Disconnect WebSocket first so websocket-gateway emits user_offline (admin sees offline)
  disconnectWebSocket()

  try {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-Tenant-ID': tenantId,
    }
    if (accessToken) {
      headers.Authorization = `Bearer ${accessToken}`
    }
    // 2) Always call logout API when we have a token so user-auth sets isOnline = false
    if (accessToken) {
      await fetch(withApiBaseUrl('/api/v1/user-auth/logout'), {
        method: 'POST',
        headers,
        body: JSON.stringify({
          ...(refreshToken ? { refreshToken } : {}),
          ...(deviceId && deviceId !== 'undefined' ? { deviceId } : {}),
        }),
        credentials: 'include',
      }).catch(() => {})
    }
  } finally {
    ;[localStorage, sessionStorage].forEach((s) => {
      s.removeItem('accessToken')
      s.removeItem('refreshToken')
      s.removeItem('deviceId')
      s.removeItem('user')
      s.removeItem('tenantId')
      s.removeItem('rememberMe')
    })
    clearRuntimeTenantId()
    invalidateUserInfoCache()
  }
}

/** อ่าน storage ตามที่ login ใช้: จำฉัน = localStorage, ไม่จำ = sessionStorage */
function getAuthStorage(): Storage {
  if (typeof window === 'undefined') return localStorage
  if (localStorage.getItem('accessToken')) return localStorage
  if (sessionStorage.getItem('accessToken')) return sessionStorage
  return localStorage
}

export function getCurrentUser(): User | null {
  try {
    const storage = getAuthStorage()
    const s = storage.getItem('user')
    return s ? (JSON.parse(s) as User) : null
  } catch {
    return null
  }
}

export function getAccessToken(): string | null {
  return localStorage.getItem('accessToken') || sessionStorage.getItem('accessToken')
}

/**
 * Apply view-as token from admin: store token, fetch profile, store user.
 * Call when URL hash contains viewAsToken (e.g. #/dashboard?viewAsToken=xxx).
 * Does not touch refresh token — view-as session is token-only and short-lived.
 */
export async function applyViewAsToken(viewAsToken: string): Promise<{ ok: boolean; error?: string }> {
  const profileUrl = withApiBaseUrl('/api/v1/user-auth/profile')
  if (import.meta.env?.DEV) {
    console.log('[ViewAs] applyViewAsToken: fetching profile', profileUrl)
  }
  try {
    localStorage.setItem('accessToken', viewAsToken)
    const res = await fetch(profileUrl, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${viewAsToken}`,
      },
      credentials: 'include',
    })
    const data = (await res.json().catch(() => ({}))) as { success?: boolean; data?: { user?: unknown }; message?: string }
    if (import.meta.env?.DEV) {
      console.log('[ViewAs] profile response:', res.status, data?.success, data?.message || '')
    }
    if (!res.ok || !data?.success || !data?.data?.user) {
      localStorage.removeItem('accessToken')
      return { ok: false, error: data?.message || 'โหลดข้อมูลผู้ใช้ไม่สำเร็จ' }
    }
    const user = data.data.user as User
    localStorage.setItem('user', JSON.stringify(user))
    localStorage.setItem('tenantId', user.tenantId || '')
    setRuntimeTenantId(user.tenantId || '')
    if (import.meta.env?.DEV) {
      console.log('[ViewAs] user stored:', user?.username, user?.tenantId)
    }
    return { ok: true }
  } catch (e) {
    if (import.meta.env?.DEV) {
      console.log('[ViewAs] applyViewAsToken error:', e)
    }
    localStorage.removeItem('accessToken')
    return { ok: false, error: e instanceof Error ? e.message : 'เกิดข้อผิดพลาด' }
  }
}

/** Parse viewAsToken from current hash (e.g. #/dashboard?viewAsToken=xxx). Returns token or null. */
export function getViewAsTokenFromHash(): string | null {
  if (typeof window === 'undefined') return null
  const hash = window.location.hash
  const qIndex = hash.indexOf('?')
  if (qIndex === -1) return null
  const params = new URLSearchParams(hash.slice(qIndex))
  const token = params.get('viewAsToken')
  if (import.meta.env?.DEV && token) {
    console.log('[ViewAs] getViewAsTokenFromHash: found token, hash length', hash.length)
  }
  return token
}

/** Alias for getAccessToken (used by chatWebSocketService / chatService) */
export function getToken(): string | null {
  return getAccessToken()
}

/** เปลี่ยนรหัสผ่าน (ต้องส่งรหัสผ่านเดิมและรหัสผ่านใหม่) — หลังสำเร็จ backend จะ invalidate refresh tokens */
export async function changePassword(currentPassword: string, newPassword: string): Promise<{ success: boolean; error?: string }> {
  const user = getCurrentUser()
  const token = getAccessToken()
  const tenantId = user?.tenantId || getAuthStorage().getItem('tenantId')
  if (!user?.id || !token || !tenantId) {
    return { success: false, error: 'ไม่พบข้อมูลผู้ใช้' }
  }
  try {
    const res = await fetch(withApiBaseUrl('/api/v1/user/change-password'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        'X-Tenant-ID': tenantId,
      },
      credentials: 'include',
      body: JSON.stringify({ currentPassword, newPassword }),
    })
    const data = (await res.json().catch(() => ({}))) as { success?: boolean; message?: string; code?: string }
    if (res.status === 401 && data?.code === 'SESSION_REPLACED') {
      await handleSessionReplaced()
      return { success: false, error: 'SESSION_REPLACED' }
    }
    if (!res.ok) {
      const msg = data?.message || (res.status === 400 ? 'รหัสผ่านเดิมไม่ถูกต้อง' : 'เปลี่ยนรหัสผ่านไม่สำเร็จ')
      return { success: false, error: msg }
    }
    return { success: true }
  } catch (e) {
    return { success: false, error: (e instanceof Error ? e.message : 'เกิดข้อผิดพลาด') }
  }
}

export function isAuthenticated(): boolean {
  const token = getAccessToken()
  const user = getCurrentUser()
  if (!token || !user) return false
  try {
    const payload = JSON.parse(atob(token.split('.')[1]))
    return payload.exp > Math.floor(Date.now() / 1000)
  } catch {
    return false
  }
}

export async function refreshToken(): Promise<{ success: boolean; data?: { token: string } }> {
  const storage = getAuthStorage()
  const refreshTokenValue = storage.getItem('refreshToken')
  const tenantId = storage.getItem('tenantId') || getCurrentUser()?.tenantId || ''

  if (!refreshTokenValue) {
    return { success: false }
  }

  try {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    }
    if (tenantId) {
      headers['X-Tenant-ID'] = tenantId
    }

    const res = await fetch(withApiBaseUrl('/api/v1/user-auth/refresh'), {
      method: 'POST',
      headers,
      body: JSON.stringify({ refreshToken: refreshTokenValue }),
      credentials: 'include',
    })

    const data = (await res.json().catch(() => ({}))) as {
      success?: boolean
      code?: string
      data?: {
        tokens?: { accessToken?: string; refreshToken?: string }
        accessToken?: string
        refreshToken?: string
      }
    }

    if (res.status === 401 && data?.code === 'SESSION_REPLACED') {
      await handleSessionReplaced()
      return { success: false }
    }

    if (!res.ok || !data.success || !data.data) {
      return { success: false }
    }

    const tokens = data.data.tokens || data.data
    if (!tokens.accessToken || !tokens.refreshToken) {
      return { success: false }
    }

    storage.setItem('accessToken', tokens.accessToken)
    storage.setItem('refreshToken', tokens.refreshToken)

    return {
      success: true,
      data: {
        token: tokens.accessToken,
      },
    }
  } catch {
    return { success: false }
  }
}

/** บัญชีธนาคารของผู้ใช้ (จาก user-info) — ใช้แสดงใน popup ถอนเงิน */
export interface UserBankAccount {
  bankCode: string
  bankName: string
  accountNumber: string
  accountName: string
  isActive?: boolean
  isDelete?: boolean
  imageUrl?: string
}

export type UserManagedServiceType = 'lottery' | 'casino'

export interface UserServiceConfig {
  serviceType: string
  isActive?: boolean
}

/** ข้อมูล user-info สำหรับแสดงยอดเครดิต/คะแนน (เรียกหลัง login) */
export interface UserInfoSummary {
  balance: number
  points: number
  serviceConfigs?: UserServiceConfig[]
}

export function isManagedServiceEnabled(
  serviceConfigs: UserServiceConfig[] | null | undefined,
  serviceType: UserManagedServiceType
): boolean {
  const target = Array.isArray(serviceConfigs)
    ? serviceConfigs.find((item) => item?.serviceType?.toLowerCase?.() === serviceType)
    : undefined
  return target ? target.isActive !== false : true
}

/**
 * ตัวดึงข้อมูล user-info แบบ raw ที่ใช้ร่วมกัน (shared)
 *
 * ทั้ง fetchUserBankAccounts / fetchUserInfoSummary / fetchUserInfoFull เรียก endpoint
 * เดียวกัน (/api/v1/user-info/:tenantId/:userId) แต่ดึงคนละฟิลด์ เดิมทำให้หน้าเดียว
 * ยิง API ซ้ำ 2-3 ครั้งพร้อมกัน ทำให้ช้าโดยไม่จำเป็น
 *
 * ใช้ in-flight dedup + cache TTL สั้นๆ เพื่อยุบหลายคำขอที่เกิดพร้อมกันให้เหลือคำขอเดียว
 */
type RawUserInfo = Record<string, any>

const USER_INFO_CACHE_TTL = 8000
let rawUserInfoCache: { key: string; ts: number; data: RawUserInfo | null } | null = null
let rawUserInfoInflight: { key: string; promise: Promise<RawUserInfo | null> } | null = null

/** ล้าง cache user-info (เรียกหลังอัปเดตข้อมูล หรือตอน logout) */
export function invalidateUserInfoCache(): void {
  rawUserInfoCache = null
  rawUserInfoInflight = null
}

async function fetchRawUserInfo(options?: { force?: boolean }): Promise<RawUserInfo | null> {
  const user = getCurrentUser()
  const token = getAccessToken()
  const tenantId = user?.tenantId || getAuthStorage().getItem('tenantId')
  if (!user?.id || !token || !tenantId) return null

  const key = `${tenantId}:${user.id}`
  const now = Date.now()

  if (!options?.force && rawUserInfoCache && rawUserInfoCache.key === key && now - rawUserInfoCache.ts < USER_INFO_CACHE_TTL) {
    return rawUserInfoCache.data
  }
  if (rawUserInfoInflight && rawUserInfoInflight.key === key) {
    return rawUserInfoInflight.promise
  }

  const promise = (async (): Promise<RawUserInfo | null> => {
    try {
      const res = await fetch(withApiBaseUrl(`/api/v1/user-info/${tenantId}/${user.id}`), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          'X-Tenant-ID': tenantId,
        },
        credentials: 'include',
      })
      const json = (await res.json().catch(() => ({}))) as { success?: boolean; code?: string; data?: RawUserInfo }
      if (res.status === 401 && json?.code === 'SESSION_REPLACED') {
        await handleSessionReplaced()
        return null
      }
      if (!res.ok || !json.success || !json.data) return null
      const data = json.data
      rawUserInfoCache = { key, ts: Date.now(), data }
      return data
    } catch {
      return null
    } finally {
      if (rawUserInfoInflight && rawUserInfoInflight.key === key) {
        rawUserInfoInflight = null
      }
    }
  })()

  rawUserInfoInflight = { key, promise }
  return promise
}

/** ดึงบัญชีธนาคารของผู้ใช้ที่ login (สำหรับรับเงินถอน) จาก user-info */
export async function fetchUserBankAccounts(): Promise<UserBankAccount[] | null> {
  const d = await fetchRawUserInfo()
  if (!d) return null
  const bankList = Array.isArray(d.bankAccount) ? d.bankAccount : Array.isArray(d.bank_account) ? d.bank_account : null
  if (!bankList) return null
  return bankList
    .filter((a: any) => a && (a.isActive ?? a.is_active) !== false && !(a.isDelete ?? a.is_delete))
    .map((a: any) => ({
      bankCode: (a.bankCode ?? a.bank_code ?? '') as string,
      bankName: (a.bankName ?? a.bank_name ?? '') as string,
      accountNumber: (a.accountNumber ?? a.account_number ?? '') as string,
      accountName: (a.accountName ?? a.account_name ?? '') as string,
      isActive: a.isActive ?? a.is_active,
      isDelete: a.isDelete ?? a.is_delete,
      imageUrl: a.imageUrl ?? a.image_url,
    })) as UserBankAccount[]
}

export async function fetchUserInfoSummary(options?: { force?: boolean }): Promise<UserInfoSummary | null> {
  try {
    const d = (await fetchRawUserInfo(options)) as {
      walletBalanceSnapshot?: number
      bonuses?: Array<{ balance?: number; points?: number }>
      loyaltyPoints?: { currentPoints?: number }
      serviceConfigs?: Array<{ serviceType?: string; isActive?: boolean }>
    } | null
    if (!d) return null
    const balance = typeof d.walletBalanceSnapshot === 'number' ? d.walletBalanceSnapshot : 0
    const points =
      typeof d.loyaltyPoints?.currentPoints === 'number'
        ? d.loyaltyPoints.currentPoints
        : Array.isArray(d.bonuses) && d.bonuses[0] != null && typeof (d.bonuses[0] as any).points === 'number'
        ? (d.bonuses[0] as any).points
        : 0
    const serviceConfigs = Array.isArray(d.serviceConfigs)
      ? d.serviceConfigs
          .filter((item): item is { serviceType?: string; isActive?: boolean } => !!item)
          .map((item) => ({
            serviceType: String(item.serviceType ?? '').toLowerCase(),
            isActive: item.isActive,
          }))
      : []
    return { balance, points, serviceConfigs }
  } catch {
    return null
  }
}

/** ข้อมูล user-info เต็ม (สำหรับหน้า member) */
export interface UserInfoFull {
  _id?: string
  userId?: string
  username: string
  mobile: string
  name: string
  email?: string
  lineAccountId?: string
  serviceConfigs?: UserServiceConfig[]
  bankAccount: Array<{
    bankCode: string
    bankName: string
    accountNumber: string
    accountName: string
    promptPayId?: string
    isActive?: boolean
    isDelete?: boolean
  }>
  trueWallet: Array<{
    trueWalletId: string
    trueWalletName: string
    trueWalletMobile: string
    isActive?: boolean
    isDelete?: boolean
  }>
}

/** ดึงข้อมูล user-info เต็มจาก API (สำหรับหน้า member) */
export async function fetchUserInfoFull(): Promise<UserInfoFull | null> {
  try {
    const d = (await fetchRawUserInfo()) as Record<string, unknown> | null
    if (!d) return null
    // รองรับทั้ง camelCase และ snake_case จาก API
    const rawLine = (d.lineAccountId ?? d.line_account_id ?? '') as string
    const lineAccountId = typeof rawLine === 'string' ? rawLine.trim() : ''
    const bankList = Array.isArray(d.bankAccount)
      ? d.bankAccount
      : Array.isArray(d.bank_account)
        ? d.bank_account
        : []
    const trueList = Array.isArray(d.trueWallet)
      ? d.trueWallet
      : Array.isArray(d.true_wallet)
        ? d.true_wallet
        : []
    const serviceConfigs = Array.isArray(d.serviceConfigs)
      ? d.serviceConfigs
          .filter((item: any) => item)
          .map((item: any) => ({
            serviceType: String(item.serviceType ?? '').toLowerCase(),
            isActive: item.isActive,
          }))
      : []
    return {
      _id: (d as any)._id,
      userId: (d as any).userId,
      username: (d.username ?? '') as string,
      mobile: (d.mobile ?? '') as string,
      name: (d.name ?? '') as string,
      email: (d.email as string | undefined),
      lineAccountId,
      serviceConfigs,
      bankAccount: bankList
        .filter((a: any) => a && (a.isDelete !== true && a.is_delete !== true))
        .map((a: any) => ({
          bankCode: (a.bankCode ?? a.bank_code ?? '') as string,
          bankName: (a.bankName ?? a.bank_name ?? '') as string,
          accountNumber: (a.accountNumber ?? a.account_number ?? '') as string,
          accountName: (a.accountName ?? a.account_name ?? '') as string,
          promptPayId: (a.promptPayId ?? a.prompt_pay_id ?? '') as string,
          isActive: a.isActive ?? a.is_active,
          isDelete: a.isDelete ?? a.is_delete,
        })),
      trueWallet: trueList
        .filter((w: any) => w && (w.isDelete !== true && w.is_delete !== true))
        .map((w: any) => ({
          trueWalletId: (w.trueWalletId ?? w.true_wallet_id ?? '') as string,
          trueWalletName: (w.trueWalletName ?? w.true_wallet_name ?? '') as string,
          trueWalletMobile: (w.trueWalletMobile ?? w.true_wallet_mobile ?? '') as string,
          isActive: w.isActive ?? w.is_active,
          isDelete: w.isDelete ?? w.is_delete,
        })),
    }
  } catch {
    return null
  }
}

/** อัปเดตข้อมูล user-info (ชื่อ, Line, บัญชีธนาคาร, TrueMoney) — สำหรับหน้า member */
export async function updateUserInfoFull(payload: {
  name?: string
  lineAccountId?: string
  bankAccount?: Array<{ bankCode: string; bankName: string; accountNumber: string; accountName: string }>
  trueWallet?: Array<{ trueWalletId: string; trueWalletName: string; trueWalletMobile: string }>
}): Promise<{ success: boolean; error?: string }> {
  const user = getCurrentUser()
  const token = getAccessToken()
  const tenantId = user?.tenantId || getAuthStorage().getItem('tenantId')
  if (!user?.id || !token || !tenantId) return { success: false, error: 'ไม่พบข้อมูลผู้ใช้' }
  try {
    const res = await fetch(withApiBaseUrl(`/api/v1/user-info/${tenantId}/${user.id}`), {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        'X-Tenant-ID': tenantId,
      },
      credentials: 'include',
      body: JSON.stringify(payload),
    })
    const data = (await res.json().catch(() => ({}))) as { success?: boolean; message?: string; code?: string; error?: string }
    if (res.status === 401 && data?.code === 'SESSION_REPLACED') {
      await handleSessionReplaced()
      return { success: false, error: 'SESSION_REPLACED' }
    }
    if (!res.ok) return { success: false, error: data?.error || data?.message || 'บันทึกไม่สำเร็จ' }
    invalidateUserInfoCache()
    return { success: true }
  } catch (e) {
    return { success: false, error: (e instanceof Error ? e.message : 'เกิดข้อผิดพลาด') }
  }
}

/**
 * ตั้งค่า PromptPay ID ลงบัญชีธนาคารหลักของลูกค้า (สำหรับหน้า member)
 * เซ็ตได้ครั้งเดียว — ถ้ามีค่าอยู่แล้วต้องให้แอดมินแก้
 */
export async function setBankPromptPay(promptPayId: string): Promise<{ success: boolean; error?: string }> {
  const user = getCurrentUser()
  const token = getAccessToken()
  const tenantId = user?.tenantId || getAuthStorage().getItem('tenantId')
  if (!user?.id || !token || !tenantId) return { success: false, error: 'ไม่พบข้อมูลผู้ใช้' }
  const normalized = String(promptPayId || '').replace(/\D/g, '')
  if (!/^(\d{10}|\d{13})$/.test(normalized)) {
    return { success: false, error: 'PromptPay ต้องเป็นเบอร์มือถือ 10 หลัก หรือเลขบัตรประชาชน 13 หลัก' }
  }
  try {
    const res = await fetch(withApiBaseUrl(`/api/v1/user-info/${tenantId}/${user.id}/bank/promptpay`), {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        'X-Tenant-ID': tenantId,
      },
      credentials: 'include',
      body: JSON.stringify({ promptPayId: normalized }),
    })
    const data = (await res.json().catch(() => ({}))) as { success?: boolean; message?: string; code?: string; error?: string }
    if (res.status === 401 && data?.code === 'SESSION_REPLACED') {
      await handleSessionReplaced()
      return { success: false, error: 'SESSION_REPLACED' }
    }
    if (!res.ok) return { success: false, error: data?.error || data?.message || 'บันทึกไม่สำเร็จ' }
    invalidateUserInfoCache()
    return { success: true }
  } catch (e) {
    return { success: false, error: (e instanceof Error ? e.message : 'เกิดข้อผิดพลาด') }
  }
}
